import { useState } from 'react';
import { X, Folder, Palette } from 'lucide-react';
import { useProjects } from '../../hooks/useProjects';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (projectId: string) => void;
}

const PROJECT_COLORS = [
  { name: 'Mist', value: '#6b6b70' },
  { name: 'Charcoal', value: '#35363a' },
  { name: 'Steel', value: '#47484c' },
  { name: 'Pewter', value: '#4c4e51' },
  { name: 'Slate', value: '#8e8e99' },
  { name: 'Silver', value: '#9ca3af' },
  { name: 'Cloud', value: '#c4c4cc' },
  { name: 'Pale', value: '#e4e4e8' },
];

export const CreateProjectModal = ({ isOpen, onClose, onSuccess }: CreateProjectModalProps) => {
  const { createProject } = useProjects();
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [selectedColor, setSelectedColor] = useState(PROJECT_COLORS[0].value);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await createProject({
        project_name: projectName.trim(),
        project_description: projectDescription.trim() || undefined,
        project_color: selectedColor
      });

      if (result.success && result.project) {
        console.log('✅ Project created successfully:', result.project.id);

        // Reset form
        setProjectName('');
        setProjectDescription('');
        setSelectedColor(PROJECT_COLORS[0].value);

        // Call success callback
        if (onSuccess) {
          onSuccess(result.project.id);
        }

        // Close modal
        onClose();
      } else {
        setError(result.error || 'Failed to create project');
      }
    } catch (err) {
      console.error('Error creating project:', err);
      setError(err instanceof Error ? err.message : 'Failed to create project');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setProjectName('');
      setProjectDescription('');
      setSelectedColor(PROJECT_COLORS[0].value);
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal-content overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-base/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-surface rounded-xl shadow-xl w-full max-w-md border border-subtle">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-subtle">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-brand/20 rounded-lg">
                <Folder className="w-6 h-6 text-brand" />
              </div>
              <h2 className="text-xl font-bold text-th-primary">
                Create New Project
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-th-secondary hover:text-th-primary transition-colors disabled:opacity-50"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-status-error/10 border border-status-error/20 rounded-xl p-4">
                <p className="text-status-error text-sm">{error}</p>
              </div>
            )}

            {/* Project Name */}
            <div>
              <label className="block text-sm font-medium text-th-primary mb-2">
                Project Name <span className="text-status-error">*</span>
              </label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g., Marketing Campaign 2024"
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-elevated border border-subtle rounded-xl text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 transition-all disabled:opacity-50"
                maxLength={100}
              />
            </div>

            {/* Project Description */}
            <div>
              <label className="block text-sm font-medium text-th-primary mb-2">
                Description (Optional)
              </label>
              <textarea
                value={projectDescription}
                onChange={(e) => setProjectDescription(e.target.value)}
                placeholder="Describe your project..."
                disabled={isSubmitting}
                rows={3}
                className="w-full px-4 py-3 bg-elevated border border-subtle rounded-xl text-th-primary placeholder-th-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand/20 transition-all disabled:opacity-50 resize-none"
                maxLength={500}
              />
              <p className="text-xs text-th-secondary mt-1">
                {projectDescription.length}/500 characters
              </p>
            </div>

            {/* Color Selection */}
            <div>
              <label className="block text-sm font-medium text-th-primary mb-3 flex items-center space-x-2">
                <Palette className="w-4 h-4" />
                <span>Project Color</span>
              </label>
              <div className="grid grid-cols-4 gap-3">
                {PROJECT_COLORS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    onClick={() => setSelectedColor(color.value)}
                    disabled={isSubmitting}
                    className={`relative h-12 rounded-lg transition-all disabled:opacity-50 ${
                      selectedColor === color.value
                        ? 'ring-2 ring-brand ring-offset-2 ring-offset-surface scale-110'
                        : 'hover:scale-105'
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  >
                    {selectedColor === color.value && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                          <div className="w-3 h-3 bg-black rounded-full" />
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-elevated border border-subtle rounded-xl p-4">
              <p className="text-xs text-th-secondary mb-3">Preview</p>
              <div className="flex items-center space-x-3">
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{
                    backgroundColor: selectedColor,
                    opacity: 0.3
                  }}
                >
                  <Folder className="w-6 h-6 text-white opacity-50" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-th-primary truncate">
                    {projectName || 'Project Name'}
                  </p>
                  {projectDescription && (
                    <p className="text-xs text-th-secondary truncate">
                      {projectDescription}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-elevated border border-subtle rounded-xl font-medium text-th-primary hover:bg-elevated/80 transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !projectName.trim()}
                className="flex-1 button-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Creating...</span>
                  </span>
                ) : (
                  'Create Project'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
